/**
 * HTML builders for individual magic-report section pages (2.2–2.14, section 3, Report Qualifications).
 *
 * Each builder takes the section's SectionFieldCollection (and any supplementary data) and
 * returns a rendered HTML string.  An empty/null/undefined section returns an empty string so
 * the caller can skip it when building dynamic section numbers.
 *
 * KEY DIFFERENCE from action-items.util.ts:
 *   - These builders show ALL field content regardless of the addToActionItems flag.
 */
import type { SectionFieldCollection, SectionFieldDTO } from '../magic-report.type';
import { FieldType } from '../magic-report.type';
import {
  DOCUMENTS_RECEIVED_SECTION_CONFIG,
  CONSTRUCTION_PROGRESS_SCHEDULE_HAS_DATES_TEXT,
  CONSTRUCTION_PROGRESS_SCHEDULE_NO_DATES_TEXT,
} from './sections.config';
import type { FieldConfig } from './DeclarativeLayoutRenderer';
import { formatISODate } from '../../../../common/utils/common.util';
import { getScheduleCommentText } from './section-utils';
import { allTestsNeverReceived, parseMaterialTestRows } from './material-testing.util';
import { generateG703IntroText, generateG703DetailText } from './StoredMaterialsSection';
import type { StoredMaterialRow } from './StoredMaterialsTable';
import { DrawLienWaiverState, makeDefaultDrawWaiver, parseDrawWaivers, statusLineFor } from './lien-waivers-draws.util';
import {
  DrawAdjustment,
  filterAdjustmentsAboveThreshold,
  formatSignedCurrency,
  resolveMinorAdjustmentsThreshold,
} from './minor-adjustments.util';

// ── Shared helpers ─────────────────────────────────────────────────────────────

const escapeHtml = (value: unknown): string =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');

const toParagraphHtml = (value?: string): string => {
  const v = (value || '').trim();
  if (!v || v.toLowerCase() === 'n/a') return '';
  return `<p style="margin: 0 0 1em 0; text-align: justify;">${escapeHtml(v).replace(/\n/g, '<br />')}</p>`;
};

const formatCurrencyExport = (value: number | string | undefined): string => {
  const n = Number(value ?? 0);
  if (!Number.isFinite(n)) return '$0.00';
  const abs = Math.abs(n);
  const formatted = abs.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return n < 0 ? `(${formatted})` : `$${formatted}`;
};

/** Append a block of HTML with a small vertical spacer if there is already content above */
const appendBlock = (current: string, html: string): string => {
  if (!html) return current;
  if (current) return `${current}<div style="margin: 0; height: 0.5em;"></div>${html}`;
  return html;
};

/** Render all Comments TextArray entries as paragraphs */
const buildCommentsHtml = (data: SectionFieldCollection | undefined): string => {
  const commentsField = data?.fields?.find((f: SectionFieldDTO) => f.fieldName === 'Comments' && f.fieldType === FieldType.TextArray);
  if (!commentsField?.entries?.length) return '';
  return commentsField.entries
    .filter(e => {
      if (e.isFromLastMonth) return false;
      const v = (e.value || '').trim();
      return v && v.toLowerCase() !== 'n/a';
    })
    .reduce((acc: string, e) => appendBlock(acc, toParagraphHtml(e.value)), '');
};

const getFieldValue = (data: SectionFieldCollection | undefined, fieldName: string): string =>
  (data?.fields?.find((f: SectionFieldDTO) => f.fieldName === fieldName)?.value || '').trim();

// ── Shared table CSS (inline) ──────────────────────────────────────────────────

const TABLE_STYLE =
  'border-collapse: collapse; margin: 0 0 0.5em 0; width: 100%; border: none; font-family: Times New Roman, serif; font-size: 16px;';
const TH_STYLE = 'border: none; padding: 4px 8px; font-weight: bold; text-align: left;';
const TH_RIGHT_STYLE = 'border: none; padding: 4px 8px; font-weight: bold; text-align: right;';
const TH_CENTER_STYLE = 'border: none; padding: 4px 8px; font-weight: bold; text-align: center;';
const TD_STYLE = 'border: none; padding: 4px 8px;';
const TD_RIGHT_STYLE = 'border: none; padding: 4px 8px; text-align: right;';
const TD_CENTER_STYLE = 'border: none; padding: 4px 8px; text-align: center;';

// ── Section header ─────────────────────────────────────────────────────────────

/**
 * Renders the blue underlined section header used at the top of every section page.
 * sectionLabel e.g. "2.2", title e.g. "Signatures and Notary"
 */
export function buildSectionHeader(sectionLabel: string, title: string): string {
  return `<div class="section-subheader">${escapeHtml(sectionLabel)}. ${escapeHtml(title)}</div>`;
}

const MAX_ATOMIC_FIRST_BLOCK_LENGTH = 400;

/**
 * Wraps arbitrary heading HTML together with the first block of body content in a
 * break-inside: avoid container so the heading is never orphaned on a page by itself.
 * Remaining body content (after the first block) is appended outside that wrapper so
 * it can flow/split across pages normally, instead of the whole section being forced
 * to jump as one atomic block.
 */
function buildHeadingWithFirstBlock(headingHtml: string, body: string): string {
  // Split body after the first complete top-level block element by tracking open/close depth.
  const blockTagRe = /<(\/?)(?:p|div|table|ul|ol)(?:\s[^>]*)?>|<(?:p|div|table|ul|ol)(?:\s[^>]*)?\/>/gi;
  let depth = 0;
  let splitAt = -1;
  let m: RegExpExecArray | null;
  while ((m = blockTagRe.exec(body)) !== null) {
    if (m[0].endsWith('/>') || m[0].startsWith('</')) {
      // self-closing or closing tag
      if (!m[0].endsWith('/>')) depth--;
      if (depth <= 0) {
        splitAt = m.index + m[0].length;
        break;
      }
    } else {
      depth++;
    }
  }
  if (splitAt === -1) {
    // No split point found — wrap everything together, unless the body itself is too long
    // to safely glue to the heading (see MAX_ATOMIC_FIRST_BLOCK_LENGTH above). In that case
    // emit the heading unwrapped — it already carries a HEADING_CLASSES class, which the
    // page-split engine's orphan-heading walk-back recognizes natively.
    const textLength = body.replace(/<[^>]*>/g, '').length;
    if (textLength > MAX_ATOMIC_FIRST_BLOCK_LENGTH) {
      return `${headingHtml}${body}`;
    }
    return `<div style="break-inside: avoid; page-break-inside: avoid;">${headingHtml}${body}</div>`;
  }
  const firstBlock = body.slice(0, splitAt);
  const rest = body.slice(splitAt);
  const firstBlockTextLength = firstBlock.replace(/<[^>]*>/g, '').length;
  if (firstBlockTextLength > MAX_ATOMIC_FIRST_BLOCK_LENGTH) {
    return `${headingHtml}${firstBlock}${rest}`;
  }
  return `<div style="break-inside: avoid; page-break-inside: avoid;">${headingHtml}${firstBlock}</div>${rest}`;
}

/**
 * Wraps a section header together with the first block of body content in a
 * break-inside: avoid container so the heading is never orphaned on a page by itself.
 * Remaining body content (after the first block) flows normally.
 */
function buildSectionWithHeader(sectionLabel: string, title: string, body: string): string {
  return buildHeadingWithFirstBlock(buildSectionHeader(sectionLabel, title), body);
}

// ── 2.2  Signatures and Notary ─────────────────────────────────────────────────

export function buildNotarySignaturesHtml(data: SectionFieldCollection | undefined, sectionLabel: string): string {
  if (!data) return '';
  const contractorContent = getFieldValue(data, 'ContractorSignatureContent');
  const archContent = getFieldValue(data, 'ArchEngSignatureContent');
  let body = '';
  if (contractorContent) body = appendBlock(body, toParagraphHtml(contractorContent));
  if (archContent) body = appendBlock(body, toParagraphHtml(archContent));
  body = appendBlock(body, buildCommentsHtml(data));

  return body ? buildSectionWithHeader(sectionLabel, 'Signatures and Notary', body) : '';
}

// ── 2.3  Change Orders ──────────────────────────────────────────────────────────

export function buildChangeOrdersHtml(
  data: SectionFieldCollection | undefined,
  sectionLabel: string,
  /** Covered draws, in print order. One table + summary is emitted per draw. */
  draws?: Array<{ drawId?: string; name?: string }>,
): string {
  if (!data?.fields?.length) return '';
  let body = '';

  const rowsField = data.fields.find((f: SectionFieldDTO) => f.fieldName === 'ChangeOrdersRows');
  const comment1 = getFieldValue(data, 'ChangeOrdersComment1');
  const comment2 = getFieldValue(data, 'ChangeOrdersComment2');

  // The comments are section-level: one set, derived from every draw's change orders.
  if (comment1) body = appendBlock(body, toParagraphHtml(comment1));
  if (comment2) body = appendBlock(body, toParagraphHtml(comment2));
  body = appendBlock(body, buildCommentsHtml(data));

  interface ChangeOrderRow {
    coNumber?: string;
    description?: string;
    doWeHaveDoc?: string;
    contractor?: string;
    architect?: string;
    owner?: string;
    prevCO?: number;
    currCO?: number;
    isLastMonth?: boolean;
    drawId?: string;
  }

  const allRows: ChangeOrderRow[] = (rowsField?.entries || [])
    .map(e => {
      try {
        return JSON.parse(e.value || '') as ChangeOrderRow;
      } catch {
        return null;
      }
    })
    .filter((r): r is ChangeOrderRow => r != null);

  if (allRows.length === 0) {
    return body ? buildSectionWithHeader(sectionLabel, 'Change Orders', body) : '';
  }

  const newChangeOrdersLabel = `<p class="italic-subheader" style="margin: 0 0 0.5em 0; text-align: justify;"><em>(New change orders in bold font:)</em></p>`;

  const safeFormat = (raw: string) => {
    const trimmed = raw.trim();
    if (!trimmed) return '';
    const n = Number(trimmed);
    return Number.isNaN(n) ? escapeHtml(trimmed) : formatCurrencyExport(n);
  };

  const buildSummaryTable = (drawId?: string) => {
    // Per-draw summary fields, falling back to the un-suffixed originals (single-draw reports and
    // anything saved before change orders became per-draw).
    const field = (base: string) => {
      const scoped = drawId ? getFieldValue(data, `${base}__${drawId}`) : '';
      return scoped || getFieldValue(data, base);
    };
    const origSum = field('ChangeOrdersSummaryOriginalContractSum');
    const netCO = field('ChangeOrdersSummaryTotalNetChangeOrder');
    const netRealloc = field('ChangeOrdersSummaryTotalNetReallocation');
    const contractSum = field('ChangeOrdersSummaryContractSumToDate');

    if (!origSum && !netCO && !netRealloc && !contractSum) return '';
    return `<table style="${TABLE_STYLE}">
  <tbody>
    <tr><td style="${TD_STYLE}">Original Contract Sum:</td><td style="${TD_RIGHT_STYLE}">${safeFormat(origSum)}</td></tr>
    <tr><td style="${TD_STYLE}">Total Net Change Order Amount:</td><td style="${TD_RIGHT_STYLE}">${safeFormat(netCO)}</td></tr>
    <tr><td style="${TD_STYLE}">Total Net Reallocation Amount:</td><td style="${TD_RIGHT_STYLE}">${safeFormat(netRealloc)}</td></tr>
    <tr><td style="${TD_STYLE}">Contract Sum To Date:</td><td style="${TD_RIGHT_STYLE}">${safeFormat(contractSum)}</td></tr>
  </tbody>
</table>`;
  };

  const buildRowsTable = (rows: ChangeOrderRow[]) => {
    const tableRows = rows
      .map(row => {
        const isCurrent = !row.isLastMonth;
        const wrapS = isCurrent ? '<strong>' : '';
        const wrapE = isCurrent ? '</strong>' : '';
        return `<tr>
  <td style="${TD_CENTER_STYLE}">${wrapS}${escapeHtml(String(row.coNumber ?? ''))}${wrapE}</td>
  <td style="${TD_STYLE}">${wrapS}${escapeHtml(String(row.description ?? ''))}${wrapE}</td>
  <td style="${TD_CENTER_STYLE}">${wrapS}${escapeHtml(String(row.doWeHaveDoc ?? ''))}${wrapE}</td>
  <td style="${TD_CENTER_STYLE}">${wrapS}${escapeHtml(String(row.contractor ?? ''))}${wrapE}</td>
  <td style="${TD_CENTER_STYLE}">${wrapS}${escapeHtml(String(row.architect ?? ''))}${wrapE}</td>
  <td style="${TD_CENTER_STYLE}">${wrapS}${escapeHtml(String(row.owner ?? ''))}${wrapE}</td>
  <td style="${TD_RIGHT_STYLE}">${wrapS}${formatCurrencyExport(row.prevCO ?? (row as any).newCO ?? 0)}${wrapE}</td>
  <td style="${TD_RIGHT_STYLE}">${wrapS}${formatCurrencyExport(row.currCO ?? 0)}${wrapE}</td>
</tr>`;
      })
      .join('');
    return `<table style="${TABLE_STYLE}">
  <thead><tr>
    <th style="${TH_CENTER_STYLE}">C.O. #</th>
    <th style="${TH_STYLE} min-width: 60px;">Change Order Description</th>
    <th style="${TH_CENTER_STYLE} min-width: 65px;">Change Order Received</th>
    <th style="${TH_CENTER_STYLE}">Signed By Contractor</th>
    <th style="${TH_CENTER_STYLE}">Signed By Architect</th>
    <th style="${TH_CENTER_STYLE}">Signed By Owner</th>
    <th style="${TH_RIGHT_STYLE} min-width: 70px;">Previous Change Orders</th>
    <th style="${TH_RIGHT_STYLE} min-width: 70px;">Current Change Orders</th>
  </tr></thead>
  <tbody>${tableRows}</tbody>
</table>`;
  };

  // Group by draw when the report covers several; otherwise emit the single table exactly as before.
  const drawList = (draws || []).filter(d => d.drawId);
  if (drawList.length > 1) {
    const firstDrawId = drawList[0].drawId;
    let isFirstDrawBlock = true;
    drawList.forEach((draw, index) => {
      const drawRows = allRows.filter(r => (r.drawId || firstDrawId) === draw.drawId);
      if (drawRows.length === 0) return;
      const label = draw.name || `Draw ${index + 1}`;
      const drawBlock = `<p style="margin: 0.75em 0 0.25em 0;"><strong>${escapeHtml(label)}</strong></p>
${buildRowsTable(drawRows)}
${buildSummaryTable(draw.drawId)}`;
      body = appendBlock(body, isFirstDrawBlock ? `${newChangeOrdersLabel}${drawBlock}` : drawBlock);
      isFirstDrawBlock = false;
    });
  } else {
    body = appendBlock(body, `${newChangeOrdersLabel}${buildRowsTable(allRows)}\n${buildSummaryTable(drawList[0]?.drawId)}`);
  }

  return body ? buildSectionWithHeader(sectionLabel, 'Change Orders', body) : '';
}

// ── Potential Change Orders ─────────────────────────────────────────────────────

export function buildPotentialChangeOrdersHtml(data: SectionFieldCollection | undefined, sectionLabel: string): string {
  if (!data) return '';

  const comment = getFieldValue(data, 'PotentialChangeOrdersComment');
  const body = toParagraphHtml(comment);
  if (!body) return '';

  return buildSectionWithHeader(sectionLabel, 'Potential Change Orders', body);
}

// ── 2.4  Reallocations ──────────────────────────────────────────────────────────

export function buildReallocationsHtml(
  data: SectionFieldCollection | undefined,
  sectionLabel: string,
  /** Covered draws (G702s), in print order. The table is split one per draw. */
  draws?: Array<{ drawId?: string; name?: string }>,
): string {
  if (!data?.fields?.length) return '';
  let body = '';

  const rowsField = data.fields.find((f: SectionFieldDTO) => f.fieldName === 'ReallocationsRows');
  const comment1 = getFieldValue(data, 'ReallocationsComment1');
  const comment2 = getFieldValue(data, 'ReallocationsComment2');

  interface ReallocationRow {
    description?: string;
    originalBudget?: number;
    totalReallocation?: number;
    percentChange?: number;
    revisedBudget?: number;
    currentReallocation?: number;
    drawId?: string;
  }

  // Only rows with a nonzero Current Reallocation this period are shown - matching the on-screen
  // table, rows folded into the historical baseline (last month's carry-over) are excluded here.
  const allRows: ReallocationRow[] = (rowsField?.entries || [])
    .map(e => {
      try {
        return JSON.parse(e.value || '') as ReallocationRow;
      } catch {
        return null;
      }
    })
    .filter((r): r is ReallocationRow => r != null && (r.currentReallocation ?? 0) !== 0);

  /** One draw's table, with its own "Total Change Amounts" footer. */
  const buildTable = (rows: ReallocationRow[]) => {
    type Totals = { originalBudget: number; totalReallocation: number; currentReallocation: number; revisedBudget: number };
    const totals: Totals = rows.reduce<Totals>(
      (acc, row) => ({
        originalBudget: acc.originalBudget + (row.originalBudget ?? 0),
        totalReallocation: acc.totalReallocation + (row.totalReallocation ?? 0),
        currentReallocation: acc.currentReallocation + (row.currentReallocation ?? 0),
        revisedBudget: acc.revisedBudget + (row.revisedBudget ?? (row.originalBudget ?? 0) + (row.currentReallocation ?? 0)),
      }),
      { originalBudget: 0, totalReallocation: 0, currentReallocation: 0, revisedBudget: 0 },
    );
    const totalPct = totals.originalBudget !== 0 ? (totals.currentReallocation / totals.originalBudget) * 100 : 0;

    const tableRows = rows
      .map(row => {
        const orig = row.originalBudget ?? 0;
        const total = row.totalReallocation ?? 0;
        const pct = row.percentChange ?? 0;
        const revised = row.revisedBudget ?? orig + (row.currentReallocation ?? 0);
        const current = row.currentReallocation ?? 0;
        return `<tr>
  <td style="${TD_STYLE}">${escapeHtml(String(row.description ?? ''))}</td>
  <td style="${TD_RIGHT_STYLE}">${formatCurrencyExport(orig)}</td>
  <td style="${TD_RIGHT_STYLE}">${formatCurrencyExport(total)}</td>
  <td style="${TD_RIGHT_STYLE}">${pct.toFixed(1)}%</td>
  <td style="${TD_RIGHT_STYLE}">${formatCurrencyExport(revised)}</td>
  <td style="${TD_RIGHT_STYLE}">${formatCurrencyExport(current)}</td>
</tr>`;
      })
      .join('');

    const totalsRow = `<tr>
  <td style="${TD_STYLE}">Total Change Amounts:</td>
  <td style="${TD_RIGHT_STYLE}">${formatCurrencyExport(totals.originalBudget)}</td>
  <td style="${TD_RIGHT_STYLE}">${formatCurrencyExport(totals.totalReallocation)}</td>
  <td style="${TD_RIGHT_STYLE}">${totalPct.toFixed(1)}%</td>
  <td style="${TD_RIGHT_STYLE}">${formatCurrencyExport(totals.revisedBudget)}</td>
  <td style="${TD_RIGHT_STYLE}">${formatCurrencyExport(totals.currentReallocation)}</td>
</tr>`;

    return `<table style="${TABLE_STYLE}">
  <thead><tr>
    <th style="${TH_STYLE}">Reallocated Line Item</th>
    <th style="${TH_RIGHT_STYLE}">Original Budget</th>
    <th style="${TH_RIGHT_STYLE}">Total Reallocations</th>
    <th style="${TH_RIGHT_STYLE}">% Change</th>
    <th style="${TH_RIGHT_STYLE}">Revised Budget</th>
    <th style="${TH_RIGHT_STYLE}">Current Reallocation</th>
  </tr></thead>
  <tbody>${tableRows}</tbody>
  <tfoot>${totalsRow}</tfoot>
</table>`;
  };

  if (allRows.length > 0) {
    const drawList = (draws || []).filter(d => d.drawId);
    if (drawList.length > 1) {
      const firstDrawId = drawList[0].drawId;
      drawList.forEach((draw, index) => {
        const drawRows = allRows.filter(r => (r.drawId || firstDrawId) === draw.drawId);
        if (drawRows.length === 0) return;
        const label = draw.name || `Draw ${index + 1}`;
        body = appendBlock(
          body,
          `<p style="margin: 0.75em 0 0.25em 0;"><strong>${escapeHtml(label)} Reallocated Line Items:</strong></p>
${buildTable(drawRows)}`,
        );
      });
    } else {
      body = appendBlock(body, buildTable(allRows));
    }
  }

  // Comments are section-level and already summed across every G702.
  if (comment1) body = appendBlock(body, toParagraphHtml(comment1));
  if (comment2) body = appendBlock(body, toParagraphHtml(comment2));
  body = appendBlock(body, buildCommentsHtml(data));

  return body ? buildSectionWithHeader(sectionLabel, 'Reallocations', body) : '';
}

// ── 2.5  Retainage ─────────────────────────────────────────────────────────────

export function buildRetainageHtml(data: SectionFieldCollection | undefined, sectionLabel: string): string {
  if (!data) return '';
  let body = '';

  for (const fieldName of ['MainComment', 'ProceduresList', 'ReleaseProceduresList', 'ReleaseComments', 'LargestReleasesComment']) {
    const v = getFieldValue(data, fieldName);
    if (v) body = appendBlock(body, toParagraphHtml(v));
  }
  body = appendBlock(body, buildCommentsHtml(data));

  return body ? buildSectionWithHeader(sectionLabel, 'Retainage', body) : '';
}

// ── 2.6  Stored Materials ──────────────────────────────────────────────────────

export function buildStoredMaterialsHtml(
  data: SectionFieldCollection | undefined,
  sectionLabel: string,
  /** Covered payapps (G702s/draws), in print order. The listed-materials table is split one per payapp. */
  draws?: Array<{ drawId?: string; name?: string }>,
): string {
  if (!data) return '';
  let body = '';

  const rowsField = data.fields?.find((f: SectionFieldDTO) => f.fieldName === 'StoredMaterialsRows');

  // Every payapp's rows pooled together, each tagged with the payapp it belongs to.
  const allRows: StoredMaterialRow[] = (rowsField?.entries || [])
    .map(e => {
      try {
        return JSON.parse(e.value || '') as StoredMaterialRow;
      } catch {
        return null;
      }
    })
    .filter((r): r is StoredMaterialRow => r != null);

  // G703 intro + detail: use the saved values (preserves manual edits); fall back to
  // deriving them from row data, same logic as the UI's auto-populate.
  // Both are section-level and derived from EVERY payapp's materials — the intro appears above
  // the tables, the detail (offsite narrative) below them.
  const g703Intro = getFieldValue(data, 'G703MaterialsIntroComment') || generateG703IntroText(allRows);
  const g703Detail = getFieldValue(data, 'G703MaterialsComment') || generateG703DetailText(allRows);
  if (g703Intro) body = appendBlock(body, toParagraphHtml(g703Intro));

  const buildTable = (rows: StoredMaterialRow[]) => {
    const tableRows = rows
      .map(row => {
        const isOffsite = row.storedOffsite === 'Yes';
        return `<tr>
  <td style="${TD_STYLE}">${escapeHtml(String(row.description ?? ''))}</td>
  <td style="${TD_STYLE}">${escapeHtml(String(row.storedOffsite ?? ''))}</td>
  <td style="${TD_RIGHT_STYLE}">${formatCurrencyExport(row.storedThisPeriod ?? 0)}</td>
  <td style="${TD_STYLE}">${isOffsite ? escapeHtml(String(row.visitedOffsite ?? '')) : '&mdash;'}</td>
  <td style="${TD_STYLE}">${isOffsite ? escapeHtml(String(row.insuranceCert ?? '')) : '&mdash;'}</td>
  <td style="${TD_STYLE}">${isOffsite ? escapeHtml(String(row.invoicePresent ?? '')) : '&mdash;'}</td>
  <td style="${TD_STYLE}">${isOffsite ? escapeHtml(String(row.photosReceived ?? '')) : '&mdash;'}</td>
</tr>`;
      })
      .join('');
    return `<table style="${TABLE_STYLE}">
  <thead><tr>
    <th style="${TH_STYLE}">Description</th>
    <th style="${TH_STYLE}">Stored Offsite?</th>
    <th style="${TH_RIGHT_STYLE}">Amount</th>
    <th style="${TH_STYLE}">Visited?</th>
    <th style="${TH_STYLE}">Insurance?</th>
    <th style="${TH_STYLE}">Invoice?</th>
    <th style="${TH_STYLE}">Rec'd Pics?</th>
  </tr></thead>
  <tbody>${tableRows}</tbody>
</table>`;
  };

  if (allRows.length > 0) {
    const drawList = (draws || []).filter(d => d.drawId);
    if (drawList.length > 1) {
      // Split per payapp — descriptions repeat across payapps, so a merged table would be ambiguous.
      const firstDrawId = drawList[0].drawId;
      drawList.forEach((draw, index) => {
        const drawRows = allRows.filter(r => (r.drawId || firstDrawId) === draw.drawId);
        if (drawRows.length === 0) return;
        const label = draw.name || `Draw ${index + 1}`;
        body = appendBlock(
          body,
          `<p style="margin: 0.75em 0 0.25em 0;"><strong>${escapeHtml(label)} G703 Listed Materials:</strong></p>
${buildTable(drawRows)}`,
        );
      });
    } else {
      body = appendBlock(body, buildTable(allRows));
    }
  }

  if (g703Detail) body = appendBlock(body, toParagraphHtml(g703Detail));

  const other = getFieldValue(data, 'OtherMaterialsList');
  if (other) body = appendBlock(body, toParagraphHtml(other));
  body = appendBlock(body, buildCommentsHtml(data));

  return body ? buildSectionWithHeader(sectionLabel, 'Stored Materials', body) : '';
}

// ── 2.7  Lien Releases / Waivers ──────────────────────────────────────────────

export interface LienWaiverExportDraw {
  drawId?: string;
  name?: string;
  applicationNumber?: string;
  periodEnding?: string;
  periodBeginning?: string;
}

/**
 * Prints the intro line, then one labelled block per covered payapp, then the single generated
 * comment that covers all of them.
 *
 * A payapp with an unconditional waiver prints its detail row; a conditional or missing one prints
 * the status line instead, matching the client's Excel output.
 */
export function buildLienWaiversHtml(
  data: SectionFieldCollection | undefined,
  sectionLabel: string,
  draws?: LienWaiverExportDraw[],
  contractorName?: string,
): string {
  if (!data) return '';
  let body = '';

  const coveredDraws = draws?.length ? draws : [];
  const storedWaivers = parseDrawWaivers(getFieldValue(data, 'LienWaiverDraws'));

  // Reports whose Lien Waivers section predates multi-draw have no LienWaiverDraws value; fall back
  // to the scalar fields as a single payapp so their output is unchanged.
  const waivers: Array<DrawLienWaiverState & { name: string; drawInfo?: LienWaiverExportDraw }> = coveredDraws.length
    ? coveredDraws.map(drawInfo => {
        const stored = storedWaivers.find(w => w.drawId === drawInfo.drawId);
        return {
          ...(stored ?? makeDefaultDrawWaiver(drawInfo.drawId || '')),
          name: drawInfo.name || '',
          drawInfo,
        };
      })
    : [
        {
          ...makeDefaultDrawWaiver(''),
          status: (getFieldValue(data, 'LienWaiverStatus') || 'Conditional') as DrawLienWaiverState['status'],
          throughCurrentPeriod: getFieldValue(data, 'ThroughCurrentPeriod') === 'true',
          isCumulative: getFieldValue(data, 'IsCumulative') === 'true',
          contractor: getFieldValue(data, 'LienWaiverContractor'),
          applicationDate: getFieldValue(data, 'LienWaiverApplicationDate'),
          applicationNumber: getFieldValue(data, 'LienWaiverApplicationNumber'),
          amount: getFieldValue(data, 'LienWaiverAmount'),
          name: '',
          drawInfo: undefined,
        },
      ];

  const isMultiDraw = waivers.length > 1;

  const above = getFieldValue(data, 'LienWaiverAboveTableComment');
  if (above) body = appendBlock(body, toParagraphHtml(above));

  waivers.forEach(waiver => {
    const label = waiver.name ? `${waiver.name} Lien Waiver` : 'Lien Waiver';
    if (isMultiDraw) {
      body = appendBlock(body, `<p style="margin: 0.75em 0 0.25em 0;"><strong>${escapeHtml(label)}:</strong></p>`);
    }

    if (waiver.status !== 'Unconditional') {
      // The status line exists to say WHICH payapp fell short, so it is only meaningful when there
      // is more than one. With a single payapp the comment already says it, and printing the line
      // would add a paragraph single-draw reports never had.
      if (isMultiDraw) body = appendBlock(body, toParagraphHtml(statusLineFor(waiver.status)));
      return;
    }

    const drawInfo = waiver.drawInfo;
    const rawThruDate = waiver.throughCurrentPeriod ? drawInfo?.periodEnding || '' : drawInfo?.periodBeginning || '';
    const formattedThruDate = rawThruDate ? formatISODate(rawThruDate) : '';
    const drawAppNum = waiver.throughCurrentPeriod ? `Application #${drawInfo?.applicationNumber || ''}` : '';

    const contractor = waiver.contractor || contractorName || '';
    const appDate = waiver.applicationDate || formattedThruDate;
    const appNum = waiver.applicationNumber || drawAppNum;
    const amount = waiver.amount;
    if (!contractor && !appDate && !appNum && !amount) return;

    body = appendBlock(
      body,
      `<table style="${TABLE_STYLE}">
  <thead><tr>
    <th style="${TH_STYLE}">Contract / Contractor</th>
    <th style="${TH_STYLE}">Application #</th>
    <th style="${TH_STYLE}">Through Date</th>
    <th style="${TH_RIGHT_STYLE}">Amount</th>
    <th style="${TH_STYLE}">Status</th>
  </tr></thead>
  <tbody>
    <tr>
      <td style="${TD_STYLE}">${escapeHtml(contractor)}</td>
      <td style="${TD_STYLE}">${escapeHtml(appNum)}</td>
      <td style="${TD_STYLE}">${escapeHtml(appDate)}</td>
      <td style="${TD_RIGHT_STYLE}">${escapeHtml(amount)}</td>
      <td style="${TD_STYLE}">${escapeHtml(waiver.status)}</td>
    </tr>
  </tbody>
</table>`,
    );
  });

  // The below-table comment is retired for multi-draw — the generated comment carries that wording
  // now. A report whose section was never reopened still has its text there, so fall back to it.
  const mainComment =
    getFieldValue(data, 'LienWaiverComment') || (storedWaivers.length === 0 ? getFieldValue(data, 'LienWaiverBelowTableComment') : '');
  if (mainComment) body = appendBlock(body, toParagraphHtml(mainComment));

  body = appendBlock(body, buildCommentsHtml(data));

  return body ? buildSectionWithHeader(sectionLabel, 'Lien Releases / Waivers', body) : '';
}

// ── 2.8  Construction Schedule ─────────────────────────────────────────────────

export function buildConstructionScheduleHtml(data: SectionFieldCollection | undefined, sectionLabel: string): string {
  if (!data) return '';
  let body = '';

  // Construction Progress Schedule preface text (top of section, before dates table).
  // Use the saved value if present; otherwise derive it from CommencementDate presence
  // (matches ScheduleSection.tsx's datesAreEstimated logic) so the text is always correct
  // even if the Schedule section was never opened in the UI and the stored DatesAreEstimated
  // field is stale.
  const constructionProgressScheduleText =
    getFieldValue(data, 'ConstructionProgressScheduleText') ||
    (() => {
      const commencementDateValue = (getFieldValue(data, 'CommencementDate') || '').trim();
      return commencementDateValue ? CONSTRUCTION_PROGRESS_SCHEDULE_HAS_DATES_TEXT : CONSTRUCTION_PROGRESS_SCHEDULE_NO_DATES_TEXT;
    })();
  body = appendBlock(body, toParagraphHtml(constructionProgressScheduleText));

  // Key dates table — always shown; blank values display as "Not Provided"
  const dateFields: { fieldName: string; label: string }[] = [
    { fieldName: 'CommencementDate', label: 'Commencement Date' },
    { fieldName: 'OriginalCompletionDate', label: 'Original Completion Date' },
    { fieldName: 'RevisedCompletionDate', label: 'Revised Completion Date' },
    { fieldName: 'CDCCEstimatedCompletionDate', label: 'CDCC Estimated Completion Date' },
    { fieldName: 'BankLoanExpirationDate', label: 'Bank Loan Expiration Date' },
  ];

  const dateRows = dateFields.map(({ fieldName, label }) => {
    const raw = getFieldValue(data, fieldName);
    const formatted = raw ? formatISODate(raw, false) || raw : 'Not Provided';
    return `<tr>
  <td style="${TD_STYLE}">${escapeHtml(label)}</td>
  <td style="${TD_STYLE}">${escapeHtml(formatted)}</td>
</tr>`;
  });

  body = appendBlock(
    body,
    `<table class="no-row-split" style="${TABLE_STYLE} break-inside: avoid; page-break-inside: avoid;">
  <tbody>${dateRows.join('')}</tbody>
</table>`,
  );

  // Primary auto-generated schedule text
  const scheduledSectionText = getFieldValue(data, 'ScheduledSectionText');
  if (scheduledSectionText) {
    body = appendBlock(body, toParagraphHtml(scheduledSectionText));
  } else {
    // Fall back to computed text from field values
    const computed = getScheduleCommentText(data);
    if (computed) body = appendBlock(body, toParagraphHtml(computed));
  }

  // Certificate of Occupancy
  const coIssued = getFieldValue(data, 'CertificateOfOccupancyIssued');
  if (coIssued && coIssued !== 'No') {
    body = appendBlock(body, toParagraphHtml(`Certificate of Occupancy Issued: ${coIssued}`));
  }

  body = appendBlock(body, buildCommentsHtml(data));

  return body ? buildSectionWithHeader(sectionLabel, 'Construction Schedule', body) : '';
}

// ── 2.9  Construction Meeting Minutes ──────────────────────────────────────────

export function buildConstructionMeetingMinutesHtml(data: SectionFieldCollection | undefined, sectionLabel: string): string {
  if (!data) return '';

  const comment = getFieldValue(data, 'ConstructionMeetingMinutesComment');
  const body = toParagraphHtml(comment);
  if (!body) return '';

  return buildSectionWithHeader(sectionLabel, 'Construction Meeting Minutes', body);
}

// ── 2.10  Materials Testing ────────────────────────────────────────────────────

const splitFailedTestSegments = (failedTests?: string): string[] =>
  (failedTests || '')
    .split(';')
    .map(segment => segment.trim())
    .filter(segment => segment.length > 0);

export function buildMaterialTestingHtml(data: SectionFieldCollection | undefined, sectionLabel: string): string {
  if (!data) return '';
  let body = '';

  const autoComment = getFieldValue(data, 'MaterialTestingAutoComment');
  if (autoComment) body = appendBlock(body, toParagraphHtml(autoComment));

  const failedComment = getFieldValue(data, 'MaterialTestingFailedTestsComment');
  if (failedComment) body = appendBlock(body, toParagraphHtml(failedComment));

  const tests = parseMaterialTestRows(data);

  const failedRows: { testName?: string; segmentText: string }[] = tests.flatMap(test =>
    splitFailedTestSegments(test.failedTests).map(segmentText => ({ testName: test.testName, segmentText })),
  );

  if (failedRows.length > 0) {
    const tableRows = failedRows
      .map(
        row => `<tr>
  <td style="${TD_STYLE}">${escapeHtml(row.testName ?? '')}</td>
  <td style="${TD_STYLE}">${escapeHtml(row.segmentText)}</td>
</tr>`,
      )
      .join('');
    body = appendBlock(
      body,
      `<table style="${TABLE_STYLE}">
  <thead><tr>
    <th style="${TH_STYLE}">Test Type</th>
    <th style="${TH_STYLE}">Failed Test No. / Information</th>
  </tr></thead>
  <tbody>${tableRows}</tbody>
</table>`,
    );
  }

  const allPassComment = getFieldValue(data, 'MaterialTestingAllPassComment');
  if (allPassComment) body = appendBlock(body, toParagraphHtml(allPassComment));

  const recommendationComment = getFieldValue(data, 'MaterialTestingRecommendationComment');
  if (recommendationComment) body = appendBlock(body, toParagraphHtml(recommendationComment));

  const providerComment = getFieldValue(data, 'MaterialTestingProviderComment');
  if (providerComment && !allTestsNeverReceived(tests)) body = appendBlock(body, toParagraphHtml(providerComment));

  body = appendBlock(body, buildCommentsHtml(data));

  return body ? buildSectionWithHeader(sectionLabel, 'Materials Testing', body) : '';
}

// ── 2.11  Previous Fundings (text only) ───────────────────────────────────────

export function buildPreviousFundingsTextHtml(
  previousFundingsNote: string | undefined,
  sectionData: SectionFieldCollection | undefined,
  sectionLabel: string,
): string {
  let body = '';
  const noteText = (previousFundingsNote || '').trim();
  if (noteText) {
    for (const paragraph of noteText.split('\n\n')) {
      const p = paragraph.trim();
      if (p) body = appendBlock(body, toParagraphHtml(p));
    }
  }
  body = appendBlock(body, buildCommentsHtml(sectionData));

  return body ? buildSectionWithHeader(sectionLabel, 'Previous Fundings', body) : '';
}

// ── 2.12  Documents Received ──────────────────────────────────────────────────

export function buildDocumentsReceivedHtml(data: SectionFieldCollection | undefined, sectionLabel: string): string {
  if (!data) return '';
  let body = '';

  const layout = DOCUMENTS_RECEIVED_SECTION_CONFIG.layout as { leftColumn?: FieldConfig[]; rightColumn?: FieldConfig[] } | undefined;
  const leftCol = layout?.leftColumn || [];
  const rightCol = layout?.rightColumn || [];

  if (leftCol.length > 0 || rightCol.length > 0) {
    const getVal = (fieldName: string) => data.fields?.find((f: SectionFieldDTO) => f.fieldName === fieldName)?.value || 'No';
    const introComment = data.fields?.find((f: SectionFieldDTO) => f.fieldName === 'DocumentsReceivedIntroComment')?.value;

    if (introComment) {
      body = appendBlock(body, toParagraphHtml(introComment));
    } else {
      const allFields = leftCol.concat(rightCol);
      const anyReceived = allFields.some((fc: FieldConfig) => getVal(fc.fieldName) === 'Yes');
      const allNo = allFields.length > 0 && allFields.every((fc: FieldConfig) => getVal(fc.fieldName) !== 'Yes');

      if (anyReceived) {
        body = appendBlock(
          body,
          toParagraphHtml(
            'We have received the following plans, specifications, and contracts in order to perform our review of the project.',
          ),
        );
      } else if (allNo) {
        body = appendBlock(
          body,
          toParagraphHtml(
            'We have received no plans, specifications or contracts as requested, but have used the plans at the site to perform our review.  We will request of the Borrower that we receive the following documents before the next application.',
          ),
        );
      }
    }

    const maxRows = Math.max(leftCol.length, rightCol.length);
    const dataRows: string[] = [];
    for (let i = 0; i < maxRows; i++) {
      const leftC = leftCol[i] as FieldConfig | undefined;
      const rightC = rightCol[i] as FieldConfig | undefined;
      dataRows.push(
        `<tr>
  <td style="${TD_STYLE}">${escapeHtml(leftC?.label ?? '')}</td>
  <td style="${TD_STYLE}">${escapeHtml(leftC ? getVal(leftC.fieldName) : '')}</td>
  <td style="${TD_STYLE}">${escapeHtml(rightC?.label ?? '')}</td>
  <td style="${TD_STYLE}">${escapeHtml(rightC ? getVal(rightC.fieldName) : '')}</td>
</tr>`,
      );
    }
    body = appendBlock(
      body,
      `<table style="${TABLE_STYLE}">
  <thead><tr>
    <th style="${TH_STYLE}">Document</th>
    <th style="${TH_STYLE}">Received</th>
    <th style="${TH_STYLE}">Document</th>
    <th style="${TH_STYLE}">Received</th>
  </tr></thead>
  <tbody>${dataRows.join('')}</tbody>
</table>`,
    );
  }

  const allDocs = getFieldValue(data, 'AllDocsComment');
  const loanDocs = getFieldValue(data, 'LoanDocsComment');
  body = appendBlock(body, toParagraphHtml(allDocs));
  body = appendBlock(body, toParagraphHtml(loanDocs));
  body = appendBlock(body, buildCommentsHtml(data));

  return body ? buildSectionWithHeader(sectionLabel, 'Documents Received', body) : '';
}

// ── 2.13  Foundation Survey ───────────────────────────────────────────────────

export function buildFoundationSurveyHtml(data: SectionFieldCollection | undefined, sectionLabel: string): string {
  if (!data) return '';
  let body = '';

  const content = getFieldValue(data, 'FoundationSurveyContent');
  if (content) body = appendBlock(body, toParagraphHtml(content));
  body = appendBlock(body, buildCommentsHtml(data));

  return body ? buildSectionWithHeader(sectionLabel, 'Foundation Survey', body) : '';
}

// ── 2.14  Initial PCR Issues ──────────────────────────────────────────────────

export function buildInitialPcrIssuesHtml(data: SectionFieldCollection | undefined, sectionLabel: string): string {
  if (!data) return '';
  let body = '';

  const primary = getFieldValue(data, 'PlanCostReviewPrimaryText');
  const secondary = getFieldValue(data, 'PlanCostReviewSecondaryText');
  if (primary) body = appendBlock(body, toParagraphHtml(primary));
  if (secondary) body = appendBlock(body, toParagraphHtml(secondary));
  body = appendBlock(body, buildCommentsHtml(data));

  return body ? buildSectionWithHeader(sectionLabel, 'Initial PCR Issues', body) : '';
}

// ── 2.15  Minor Adjustments ───────────────────────────────────────────────────

/**
 * Prints the boilerplate, then one labelled table per payapp whose adjustment exceeds the threshold,
 * then the section's comments.
 *
 * The amounts are not stored in section fields — they come from each payapp's
 * `budgetSummary.adjustmentsByCDCC`, so the draw list has to be supplied. Reports built without it
 * print the boilerplate and comments only, exactly as before.
 */
export function buildMinorAdjustmentsHtml(
  data: SectionFieldCollection | undefined,
  sectionLabel: string,
  draws?: DrawAdjustment[],
): string {
  if (!data) return '';
  let body = '';

  const text = getFieldValue(data, 'MinorAdjustmentsText');
  if (text) body = appendBlock(body, toParagraphHtml(text));

  const threshold = resolveMinorAdjustmentsThreshold(getFieldValue(data, 'Threshold'));
  const aboveThreshold = filterAdjustmentsAboveThreshold(draws ?? [], threshold);

  aboveThreshold.forEach(adjustment => {
    const label = adjustment.name ? `${adjustment.name} Minor Adjustments` : 'Minor Adjustments';
    body = appendBlock(
      body,
      `<p style="margin: 0.75em 0 0.25em 0;"><strong>${escapeHtml(label)}:</strong></p>
<table style="${TABLE_STYLE}">
  <tbody>
    <tr>
      <td style="${TD_STYLE}">CDCC Adjustments:</td>
      <td style="${TD_RIGHT_STYLE}">${escapeHtml(formatSignedCurrency(adjustment.amount))}</td>
    </tr>
  </tbody>
</table>`,
    );
  });

  body = appendBlock(body, buildCommentsHtml(data));

  return body ? buildSectionWithHeader(sectionLabel, 'Minor Adjustments', body) : '';
}

// ── User-created dynamic sections ──────────────────────────────────────────────

/** Parse a dynamic section's table columns/rows fields (JSON-stringified) into renderable HTML */
function buildDynamicTableHtml(data: SectionFieldCollection): string {
  const columnsField = data.fields?.find((f: SectionFieldDTO) => f.fieldName === 'dynamicTableColumns');
  const rowsField = data.fields?.find((f: SectionFieldDTO) => f.fieldName === 'dynamicTableRows');
  if (!columnsField?.value) return '';

  let columns: Array<{ id: string; header: string; type: string }>;
  try {
    columns = JSON.parse(columnsField.value);
  } catch {
    return '';
  }
  if (!Array.isArray(columns) || columns.length === 0) return '';

  const rows = (rowsField?.entries || [])
    .map(e => {
      try {
        return JSON.parse(e.value || '{}');
      } catch {
        return null;
      }
    })
    .filter(Boolean);
  if (rows.length === 0) return '';

  const headerHtml = columns
    .map(col => {
      const style = col.type === 'numeric' || col.type === 'currency' ? TH_RIGHT_STYLE : TH_STYLE;
      return `<th style="${style}">${escapeHtml(col.header)}</th>`;
    })
    .join('');

  const rowsHtml = rows
    .map(row => {
      const cellsHtml = columns
        .map(col => {
          const raw = row[col.id];
          if (col.type === 'currency') {
            return `<td style="${TD_RIGHT_STYLE}">${formatCurrencyExport(raw as number)}</td>`;
          }
          if (col.type === 'numeric') {
            return `<td style="${TD_RIGHT_STYLE}">${escapeHtml(raw ?? '')}</td>`;
          }
          return `<td style="${TD_STYLE}">${escapeHtml(raw ?? '')}</td>`;
        })
        .join('');
      return `<tr>${cellsHtml}</tr>`;
    })
    .join('');

  return `<table style="${TABLE_STYLE}"><thead><tr>${headerHtml}</tr></thead><tbody>${rowsHtml}</tbody></table>`;
}

/**
 * Renders every user-created dynamic section (header + body + table + comments), regardless of
 * addToActionItems bookmark state — matching the "show everything" convention of this file.
 *
 * Each populated dynamic section gets its own flat "X.Y" label, continuing the same numbering
 * sequence as every other builder in buildMagicSection2Html — e.g. sections 2.9, 2.10, 2.11 — via
 * `startSubCounter` (the running `subCounter` at the point this is called). Returns the
 * concatenated HTML plus the count of populated sections, so the caller can advance its counter
 * by that many slots.
 */
export function buildDynamicSectionsHtml(
  data: SectionFieldCollection[] | undefined,
  majorCounter: number,
  startSubCounter: number,
): { html: string; count: number } {
  if (!data?.length) return { html: '', count: 0 };

  const parts: string[] = [];
  let subCounter = startSubCounter;

  data.forEach((section, index) => {
    if (!section?.fields?.length) return;

    const title = getFieldValue(section, 'header') || `Section ${index + 1}`;
    const bodyText = getFieldValue(section, 'body');

    let body = '';
    if (bodyText) body = appendBlock(body, toParagraphHtml(bodyText));
    body = appendBlock(body, buildDynamicTableHtml(section));
    body = appendBlock(body, buildCommentsHtml(section));

    if (!body) return;

    parts.push(buildSectionWithHeader(`${majorCounter}.${subCounter}`, title, body));
    subCounter++;
  });

  return { html: parts.join('<div style="margin-top: 1.5em;"></div>'), count: subCounter - startSubCounter };
}

// ── Section 3  Description of Construction Progress ───────────────────────────

export function buildProgressDescriptionHtml(data: SectionFieldCollection | undefined, sectionLabel: string): string {
  if (!data) return '';
  let body = '';

  const current = getFieldValue(data, 'CurrentProgress');
  if (current) body = appendBlock(body, toParagraphHtml(current));
  body = appendBlock(body, buildCommentsHtml(data));

  if (!body) return '';
  // Only the heading + first block are protected from an orphaning page break (matching
  // every other section via buildSectionWithHeader) — the rest of the body flows/splits
  // normally instead of forcing the whole section to jump as one atomic block.
  const headingHtml = `<div class="major-section-header">${escapeHtml(sectionLabel)}. DESCRIPTION OF CONSTRUCTION PROGRESS</div>`;
  return buildHeadingWithFirstBlock(headingHtml, body);
}

// ── Section 5  Report Qualifications (static, always appended) ─────────────────

export function buildReportQualificationsPart1Html(sectionNum: number): string {
  return `
<div class="section-header">${sectionNum}. REPORT QUALIFICATIONS</div>
<p style="margin: 0 0 1em 0; text-align: justify;">Following are qualifications and explanations of the comments and claims written in the Report above.</p>

<p style="margin: 0 0 1em 0; text-align: justify;"><strong>${sectionNum}.1.</strong>&nbsp; The control and acceptance of the quality of construction materials and labor, according to the contract for construction, are specifically the responsibility of the general contractor and those contractors and suppliers hired by the general contractor.&nbsp; Subsequent quality controls are the responsibility of the professional designers of record (the architect and engineers), materials testing engineers (reporting to the designers of record for particular compliance with their specifications), as well as inspectors from authorities with jurisdiction (city inspectors), who certify the general contractor's work progress.&nbsp; CDCC's responsibilities related to quality control is to confirm that the above quality control systems are in place and to rely upon those systems.</p>
<p style="margin: 0 0 1em 0; text-align: justify;">CDCC, in our role as construction risk managers for our clients, further reviews the construction work we observe, typically only on a monthly basis, for substantial compliance with the construction plans provided to us, considering the size, worth, importance, or essential items outlined in the plans.&nbsp; CDCC is not responsible for ADA, TAS. Materials testing results, or building code inspections but relies on the professionals who inspect and review the project for these requirements.</p>
<p style="margin: 0 0 1em 0; text-align: justify;">Should any Soft Costs be part of our recommendation for funding, it is with the understanding that CDCC is reviewing those costs generally and related to the construction project (professional fees, hard cost contingencies, and similar costs), and not financing, interest, or other, costs which may be of particular interest, or under the control, of the client.&nbsp; Review and approval of any Soft Costs are for general % complete. Receipts may be reviewed for association with the project or the current application, but are not tallied to confirm totals.</p>

<p style="margin: 0 0 1em 0; text-align: justify;"><strong>${sectionNum}.2.</strong>&nbsp; Once the project reaches substantial completion, it is CDCC policy to confirm with the client their own requirements before a recommendation to release any retainage.&nbsp; CDCC's recommendations are to be provided with the following:</p>
<p style="margin: 0 0 1em 0; text-align: justify;"><strong>${sectionNum}.2.1.</strong>&nbsp; A certified, separate payment application for the release of retainage only.</p>
<p style="margin: 0 0 1em 0; text-align: justify;"><strong>${sectionNum}.2.2.</strong>&nbsp; A fully executed certificate of substantial completion or a permanent certificate of occupancy or a temporary certificate of occupancy stating the requirements remaining to receive the permanent certificate of occupancy, with the release of retainage occurring 30 days past their date of issuance.</p>
<p style="margin: 0 0 1em 0; text-align: justify;"><strong>${sectionNum}.2.3.</strong>&nbsp; An unconditional lien release from the general contractor from the previous payment application with language stating that the general contractor has paid all obligations in regard to that payment application.</p>
<p style="margin: 0 0 1em 0; text-align: justify;"><strong>${sectionNum}.2.4.</strong>&nbsp; A conditional lien release for the retainage payment application, and an "as built" survey of the entire site with all permanent structures in place and showing no encroachments over restrictive areas.</p>
<p style="margin: 0 0 1em 0; text-align: justify;">We further recommend the client perform their own title search for any lien filings prior to release of retainage by the client.</p>
`.trim();
}

export function buildReportQualificationsPart2Html(sectionNum: number): string {
  return `
<p style="margin: 0 0 1em 0; text-align: justify;"><strong>${sectionNum}.3.</strong>&nbsp; Provided under separate cover is a copy of our Application for Payment Review Form.&nbsp; We use this Form to verify the contractor's Original Schedule of Values (line item budgets), current and previously approved Change Orders, any Budget Reallocations, current Draw Request Amounts, Previously Funded Amounts, Percentages Complete for each line item budget, Funds Remaining in each line item to complete the Work, Retainage, etc.&nbsp; If you have any questions regarding this Review Form, please notify your CDCC Representative.</p>

<p style="margin: 0 0 1em 0; text-align: justify;"><strong>${sectionNum}.4.</strong>&nbsp; All On-Site materials have been stored in a manner standard for the construction industry to avoid damage or theft. Please note that the reasonableness of storage and security rely on the type of material being stored.&nbsp; For example, a large, extremely heavy item, which takes special equipment to remove, could be considered reasonably secure although not surrounded by a security fence.&nbsp; Fencing only secures the boundaries of the site and can be easily removed or destroyed.&nbsp; Also, stored material that will be exposed to weather after erection or placement into the Work can, in many cases, also be exposed to the elements while stored on site.&nbsp; Builders Risk insurance, although not the best security for financial risks, will also cover damages or losses within the construction area. Note that materials that were specifically requested by the Contractor on their previous application for payment are now being incorporated into the work.</p>

<p style="margin: 0 0 1em 0; text-align: justify;"><strong>${sectionNum}.5.</strong>&nbsp; Please note that we do not review Lien Release(s) for compliance with applicable lien laws, but leave any such a review to the lender's legal counsel.&nbsp; We are only reviewing lien releases for the following:&nbsp; Reference to correct project or borrower, notarized signatures as is typically required by loan documents, whether full payment has been received by the General Contractor for the proper construction period or application number, assurance of subsequent payment to Subcontractors and Suppliers, and whether conditions have been met to make any conditional lien waiver or release effective.&nbsp; Should the lender request unconditional lien releases from major Subcontractors, please inform us and we will request appropriate tracking documentation from the general contractor.</p>

<p style="margin: 0 0 1em 0; text-align: justify;"><strong>${sectionNum}.6.</strong>&nbsp; Although it may be distributed to other parties, this report is exclusively for the use of our client named in the report, and creates no privity of contract with any other party. Without a written and executed agreement with CDCC, this document may not be used, or relied upon, and we shall incur no liability, and disclaim any responsibility for the use of, or reliance upon, this report by any other party.</p>
`.trim();
}
